import type { QueryClient } from "@tanstack/react-query";

// Query-key factories for the three counterparty records (brokerKeys-style).
// Every counterparty read registers under its `*.all` prefix so a mutation
// can cover list + detail with ONE prefix invalidation. Stale-after-save
// sweep (PR #648): mutations also invalidate the cross-surface reads that
// render counterparty names — the batched owner-label resolver
// (src/api/ownerLabels.ts, key ["owner-labels", refKey]) and the type-scoped
// counterparty typeahead (loanDetail/parties/CounterpartyPicker.tsx, key
// ["counterparty-search", type, term]) — via invalidateCounterpartyReads.

interface ListFilters {
  search: string;
  archiveScope: "Active" | "All" | "Archived";
  page: number;
}

export const personKeys = {
  all: ["persons"] as const,
  list: (filters: ListFilters) => [...personKeys.all, "list", filters] as const,
  detail: (id: string | undefined) => [...personKeys.all, "detail", id] as const,
};

export const entityKeys = {
  all: ["entities"] as const,
  list: (filters: ListFilters) => [...entityKeys.all, "list", filters] as const,
  detail: (id: string | undefined) => [...entityKeys.all, "detail", id] as const,
};

export const businessKeys = {
  all: ["businesses"] as const,
  list: (filters: ListFilters) => [...businessKeys.all, "list", filters] as const,
  detail: (id: string | undefined) => [...businessKeys.all, "detail", id] as const,
};

type CounterpartyKind = "person" | "entity" | "business";

// CounterpartyPicker's per-type search adapters key on the wire type names.
const SEARCH_TYPE: Record<CounterpartyKind, string> = {
  person: "Person",
  entity: "LegalEntity",
  business: "Business",
};

const ALL_KEYS: Record<CounterpartyKind, readonly string[]> = {
  person: personKeys.all,
  entity: entityKeys.all,
  business: businessKeys.all,
};

/** Invalidates every read that renders this counterparty kind after a
 * create / update / archive: the module's own list + detail family, the
 * batched owner-label resolver, and the counterparty typeahead. Loan party
 * rosters and deal counterparty cards cache server-resolved copies under
 * loan/deal keys we can't enumerate here — those expire via staleTime. */
export function invalidateCounterpartyReads(
  queryClient: QueryClient,
  kind: CounterpartyKind,
): void {
  void queryClient.invalidateQueries({ queryKey: ALL_KEYS[kind] });
  void queryClient.invalidateQueries({ queryKey: ["owner-labels"] });
  void queryClient.invalidateQueries({
    queryKey: ["counterparty-search", SEARCH_TYPE[kind]],
  });
}
